import { word } from "@modules/core/utils/i18n"
import { ProgramBundleForm, type ProgramBundleFormProps } from "@modules/programBundle/components/programBundleForm"
import { ProgramBundleInfoWrapper } from "@modules/programBundle/components/programBundleInfoWrapper"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { type SidebarNavigatorParams } from "@modules/sidebar/sidebarTypes"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { ErrorBoundary } from "react-error-boundary"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Suspense } from "react"
import { LoadingSpinner } from "@/modules/ui/components/loading"

type navigateToProgramBundleFormProps = {
  programBundleFormProps?: ProgramBundleFormProps
  args?: SidebarNavigatorParams
}

export const useProgramBundleSidebar = () => {
  const { sidebarNavigateTo, hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()

  const navigateToProgramBundleForm = ({ programBundleFormProps, args }: navigateToProgramBundleFormProps) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramBundleForm {...programBundleFormProps} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: async () =>
        await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: programBundleFormProps?.programBundle
              ? word("programBundle.form.cancelEdit.title")
              : word("programBundle.form.cancel.title"),
            message: programBundleFormProps?.programBundle
              ? word("programBundle.form.cancelEdit.message")
              : word("programBundle.form.cancel.message"),
            accept: () => {
              return Promise.resolve(void resolve(true))
            },
            cancel: () => {
              return Promise.resolve(void resolve(false))
            },
          })
        }),
      ...args,
    })
  }

  const navigateToProgramBundleResume = (programBundleId: string, args?: SidebarNavigatorParams) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramBundleInfoWrapper programBundleId={programBundleId} />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  return { hideSidebar, navigateToProgramBundleResume, navigateToProgramBundleForm }
}
